import React from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import { AnimatePresence, motion, easeInOut } from "framer-motion";
import Button from "../Button/Button"; // novo import

/**
 * Loader - Spinner profissional reutilizável
 *
 * Um componente de carregamento acessível e personalizável, compatível com temas claros/escuros, overlay, cor de destaque e mensagens.
 * Agora com fallback animado de três pontinhos para integração com Suspense/React.lazy.
 * Suporte a tamanho responsivo automático via prop `responsive`.
 * Permite children customizados (ícone, mensagem, etc) abaixo ou no lugar do spinner.
 * Suporte a delay opcional para evitar flicker (prop delayMs).
 * Suporte a texto alternativo customizável via prop ariaLabel.
 * Foco automático para leitores de tela ao aparecer (caso overlay).
 *
 * @component
 * @example
 * // Loader simples
 * <Loader />
 *
 * @example
 * // Loader overlay com mensagem
 * <Loader overlay message="Carregando..." />
 *
 * @example
 * // Loader responsivo (tamanho menor em telas pequenas)
 * <Loader responsive />
 *
 * @example
 * // Loader com fallback de três pontinhos (Suspense)
 * <Loader fallbackType="dots" />
 *
 * @example
 * // Loader customizado
 * <Loader size={64} accentColor="#ff9800" theme="dark" message="Aguarde..." />
 *
 * @example
 * // Loader com delay para evitar flicker
 * <Loader delayMs={300} />
 *
 * @example
 * // Loader com children customizado (ícone e mensagem)
 * <Loader>
 *   <div>
 *     <MyCustomIcon />
 *     <span>Processando pagamento...</span>
 *   </div>
 * </Loader>
 *
 * @example
 * // Loader com texto alternativo customizado para acessibilidade
 * <Loader overlay ariaLabel="Carregando relatórios financeiros" />
 *
 * @param {object} props
 * @param {number} [props.size=40] Tamanho do spinner em pixels.
 * @param {string} [props.className] Classe CSS extra para o container.
 * @param {boolean} [props.overlay=false] Se verdadeiro, exibe overlay centralizado na tela.
 * @param {string} [props.message] Mensagem opcional exibida abaixo do spinner.
 * @param {string} [props.accentColor] Cor do spinner (hex/rgb/etc). Usa cor padrão do tema se não informado.
 * @param {"light"|"dark"} [props.theme="light"] Tema (ajusta cor de fundo e texto no overlay).
 * @param {string} [props.spinnerClassName] Classe CSS extra para o SVG.
 * @param {"spinner"|"dots"} [props.fallbackType="spinner"] Tipo de fallback: spinner ou animação de três pontinhos.
 * @param {boolean} [props.responsive=false] Se verdadeiro, adapta o tamanho do loader automaticamente para mobile.
 * @param {number} [props.delayMs] Delay em ms para exibir o loader (evita flicker).
 * @param {React.ReactNode} [props.children] Conteúdo customizado exibido abaixo ou no lugar do spinner.
 * @param {string} [props.ariaLabel] Texto alternativo para acessibilidade (aria-label).
 * @param {number} [props.progress] 0..1 para modo determinate.
 * @param {boolean} [props.reducedMotion] override manual.
 * @param {boolean} [props.lockScroll=true] bloqueia scroll ao usar overlay.
 * @param {string} [props.portalId="loader-portal"] id custom do portal.
 * @param {function} [props.onCancel] callback botão cancelar.
 * @param {string} [props.cancelLabel="Cancelar"] texto do botão cancelar.
 * @param {number} [props.zIndex=50] custom z-index.
 * @returns {JSX.Element|null}
 */
const Loader: React.FC<{
  size?: number;
  className?: string;
  overlay?: boolean;
  message?: string;
  accentColor?: string;
  theme?: "light" | "dark";
  spinnerClassName?: string;
  fallbackType?: "spinner" | "dots";
  responsive?: boolean;
  delayMs?: number;
  children?: React.ReactNode;
  ariaLabel?: string;
  progress?: number;            // 0..1 para modo determinate
  reducedMotion?: boolean;      // override manual
  lockScroll?: boolean;         // bloqueia scroll ao usar overlay
  portalId?: string;            // id custom do portal
  onCancel?: () => void;        // callback botão cancelar
  cancelLabel?: string;         // texto do botão
  zIndex?: number;              // custom z-index
  // Permite props extras
  [key: string]: unknown;
}> = ({
  size = 40,
  className,
  overlay = false,
  message,
  accentColor,
  theme = "light",
  spinnerClassName,
  fallbackType = "spinner",
  responsive = false,
  delayMs,
  children,
  ariaLabel,
  progress,
  reducedMotion,
  lockScroll = true,
  portalId = "loader-portal",
  onCancel,
  cancelLabel = "Cancelar",
  zIndex = 50,
  ...rest // <- props extras
}) => {
  const [computedSize, setComputedSize] = React.useState(size);
  const [show, setShow] = React.useState(delayMs ? false : true);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [prefersReduced, setPrefersReduced] = React.useState(false);

  React.useEffect(() => {
    if (!responsive) return;
    const updateSize = () => {
      if (window.innerWidth < 640) setComputedSize(Math.max(28, size * 0.7));
      else setComputedSize(size);
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [responsive, size]);

  React.useEffect(() => {
    if (!delayMs) return;
    setShow(false);
    const timer = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  // Foco automático para leitores de tela ao aparecer (caso overlay)
  React.useEffect(() => {
    if (show && overlay && containerRef.current) {
      containerRef.current.focus();
    }
  }, [show, overlay]);

  // Focus trap para overlay (acessibilidade extra)
  React.useEffect(() => {
    if (!show || !overlay || !containerRef.current) return;

    const node = containerRef.current;
    const focusableSelectors =
      'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';
    const focusables = node.querySelectorAll<HTMLElement>(focusableSelectors);

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab" || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    }

    node.addEventListener("keydown", handleKeyDown);
    return () => node.removeEventListener("keydown", handleKeyDown);
  }, [show, overlay]);

  // Detecta prefers-reduced-motion (se prop não for explicitada)
  React.useEffect(() => {
    if (typeof reducedMotion === "boolean") return;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReduced(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    } else if (mq.addListener) {
      // fallback legacy
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, [reducedMotion]);

  const isReducedMotion = typeof reducedMotion === "boolean" ? reducedMotion : prefersReduced;
  const isDeterminate = typeof progress === "number" && progress >= 0 && progress <= 1;

  // Bloqueia scroll quando overlay ativo
  React.useEffect(() => {
    if (!overlay || !show || !lockScroll) return;
    if (typeof document === "undefined") return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [overlay, show, lockScroll]);

  if (!show) return null;

  const spinnerColor = accentColor || (theme === "dark" ? "#38bdf8" : "#1976d2");
  // Animação de fade/scale para entrada/saída
  const animationProps = {
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.96 },
    transition: { duration: 0.22, ease: easeInOut } // corrigido para função aceita pelo Framer Motion
  };

  const circumference = 2 * Math.PI * 16;
  const determinateStroke = isDeterminate
    ? {
        strokeDasharray: `${Math.min(1, Math.max(0, progress!)) * circumference} ${circumference}`
      }
    : undefined;

  const loaderContent = (
    <AnimatePresence mode="wait">
      <motion.div
        key="loader"
        {...animationProps}
        ref={containerRef}
        className={clsx(
          overlay
            ? [
                "fixed inset-0 flex flex-col items-center justify-center",
                theme === "dark"
                  ? "bg-black/70 backdrop-blur-sm"
                  : "bg-black/30 backdrop-blur-sm"
              ]
            : "inline-flex flex-col items-center justify-center",
          className
        )}
        role={isDeterminate ? "progressbar" : "status"}
        aria-live={isDeterminate ? undefined : "polite"}
        aria-busy={isDeterminate ? undefined : "true"}
        aria-valuenow={isDeterminate ? Math.round((progress ?? 0) * 100) : undefined}
        aria-valuemin={isDeterminate ? 0 : undefined}
        aria-valuemax={isDeterminate ? 100 : undefined}
        tabIndex={overlay ? 0 : -1}
        aria-label={ariaLabel || message || (isDeterminate ? "Progresso" : "Carregando")}
        data-testid="loader"
        style={{ zIndex, ...(rest.style as React.CSSProperties) }}
        {...rest}
      >
        {fallbackType === "spinner" ? (
          <svg
            width={computedSize}
            height={computedSize}
            viewBox="0 0 40 40"
            className={clsx(!isDeterminate && !isReducedMotion && "animate-spin", spinnerClassName)}
            aria-label={ariaLabel || message || (isDeterminate ? "Progresso" : "Carregando")}
            focusable="false"
            style={{ color: spinnerColor }}
          >
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              opacity="0.18"
            />
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
              {...(isDeterminate
                ? determinateStroke
                : { pathLength: 1, strokeDasharray: "40 64" })}
              style={isDeterminate ? undefined : { transformOrigin: "center" }}
            />
          </svg>
        ) : (
          <ThreeDots
            color={spinnerColor}
            size={computedSize}
            theme={theme}
          />
        )}
        {message && (
          <span
            className={clsx(
              "mt-3 text-base font-medium select-none text-center px-2",
              theme === "dark" ? "text-gray-200" : "text-gray-700"
            )}
          >
            {message}
          </span>
        )}
        {children && (
          <div className="mt-3 w-full flex flex-col items-center justify-center">
            {children}
          </div>
        )}
        {onCancel && (
          // substituído botão nativo por Button reutilizável
          <Button
            className="mt-4"
            size="small"
            variant="outlined"
            theme={theme}
            title={cancelLabel}
            ariaLabel={cancelLabel}
            onClick={() => onCancel?.()}
            // opcional: estilização extra mantendo contraste
            buttonClassName={clsx(
              "min-w-[110px]",
              theme === "dark"
                ? "bg-gray-200 text-gray-800 hover:bg-white"
                : "bg-white text-gray-900 hover:bg-gray-100"
            )}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );

  // Se overlay, renderiza em portal para garantir sobreposição global
  if (overlay) {
    const portalTarget = getOrCreateLoaderPortal(portalId);
    if (portalTarget) {
      return ReactDOM.createPortal(loaderContent, portalTarget);
    }
    // SSR: não renderiza nada (ou pode retornar null)
    return null;
  }

  return loaderContent;
};

export default Loader;

/**
 * Componente animado de três pontinhos
 * Perfeito para fallback de Suspense (leve, acessível e elegante)
 *
 * @param {object} props
 * @param {string} props.color Cor dos pontinhos.
 * @param {number} [props.size=40] Tamanho base dos pontinhos.
 * @param {"light"|"dark"} [props.theme="light"] Tema.
 * @returns {JSX.Element}
 * @example
 * <ThreeDots color="#1976d2" size={32} theme="dark" />
 */
const ThreeDots: React.FC<{
  color: string;
  size?: number;
  theme?: "light" | "dark";
}> = ({ color, size = 40, theme = "light" }) => (
  <div
    className={clsx("flex items-center h-[40px]")}
    role="presentation"
    style={{ height: size, minWidth: size / 2 }}
  >
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className={clsx(
          "inline-block rounded-full mx-[2px]",
          theme === "dark" ? "bg-gray-200" : "bg-gray-700"
        )}
        style={{
          width: size * 0.18,
          height: size * 0.18,
          backgroundColor: color,
          animation: "loader-dot-bounce 1s infinite",
          animationDelay: `${i * 0.2}s`
        }}
      />
    ))}
    {/* Animação via CSS-in-JS (pode ser tema global também) */}
    <style>
      {`
      @keyframes loader-dot-bounce {
        0%, 80%, 100% { transform: scale(0.7); opacity: 0.7; }
        40% { transform: scale(1.12); opacity: 1; }
      }
      `}
    </style>
  </div>
);

// Utilitário para garantir um único portal target
function getOrCreateLoaderPortal(id = "loader-portal") {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  let portal = document.getElementById(id);
  if (!portal) {
    portal = document.createElement("div");
    portal.id = id;
    document.body.appendChild(portal);
  }
  return portal;
}